import React, { useState, useRef, useMemo } from "react";
import {
  TextField,
  Stack,
  IconButton,
  useTheme,
  Box,
  Popper,
  Paper,
  MenuList,
  ClickAwayListener,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Button,
  ButtonGroup,
  Menu,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachmentIcon from "@mui/icons-material/Attachment";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { UserStyleModel } from "../models/StylesModels";
import UserStyles from "./UserStyles";
import LanguageSelect from "./LanguageSelect";
import ChatType, { ChatTypeEnum } from "./ChatType";
import ChatHistory from "./ChatHistory";
import { ChatMessage } from "./ChatMessage";
import { useChatEmail } from "../hooks/useChatEmail";
import { getReadingEmailBody, getReadingEmailRestId, getReadingSubject } from "../taskpane";
import ReceivablesSelectionPopper from "./ReceivablesSelectionDialog";
import { SearchInvoiceReceivableResponseDetailedModel } from "../models/SearchModels";
import { useReadingEmail } from "../hooks/useReadingEmail";
import { useContacts } from "../hooks/useContacts";
import { ContactType } from "../models/ContactModels";
import { OrganisationFileResponse } from "../models/OrganisationModels";
import { OrganisationFilePicker } from "./OrganisationFilePicker/OrganisationFilePicker";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { LoadingButton } from "./LoadingButton";
import { RichTextEditor } from "./RichTextEditor";
import { DraftingOverlay } from "./DraftingOverlay";
import { isEmptyEmailHtml } from "../utils/emailHtml";

interface EmailChatProps {
  onSendMessage?: (
    message: string,
    style: UserStyleModel | null,
    type: ChatTypeEnum,
    attachments?: SearchInvoiceReceivableResponseDetailedModel[],
    organisationFiles?: OrganisationFileResponse[],
    isReplyAll?: boolean,
    language?: string
  ) => Promise<void>;
  onRedraft?: (
    prompt: string,
    draftHtml: string,
    style: UserStyleModel | null,
    type: ChatTypeEnum,
    attachments?: SearchInvoiceReceivableResponseDetailedModel[],
    organisationFiles?: OrganisationFileResponse[],
    isReplyAll?: boolean,
    language?: string
  ) => Promise<void>;
  onSendDraft?: (
    draftHtml: string,
    style: UserStyleModel | null,
    type: ChatTypeEnum,
    attachments?: SearchInvoiceReceivableResponseDetailedModel[],
    organisationFiles?: OrganisationFileResponse[],
    isReplyAll?: boolean,
    language?: string
  ) => Promise<void>;
  isLoading?: boolean;
  placeholder?: string;
  draftReviewPlaceholder?: string;
  maxLength?: number;
  value?: string;
  onValueChange?: (value: string) => void;
  promptValue?: string;
  onPromptChange?: (value: string) => void;
  draftValue?: string;
  onDraftChange?: (value: string) => void;
  isDraftReady?: boolean;
  chatType?: ChatTypeEnum;
  onChatTypeChange?: (type: ChatTypeEnum) => void;
}

const EmailChat: React.FC<EmailChatProps> = ({
  onSendMessage,
  onRedraft,
  onSendDraft,
  isLoading = false,
  placeholder = "Write an email regarding ...",
  draftReviewPlaceholder = "Review and edit your draft before sending...",
  maxLength = 255,
  value,
  onValueChange,
  promptValue,
  onPromptChange,
  draftValue,
  onDraftChange,
  isDraftReady = false,
  chatType,
  onChatTypeChange,
}) => {
  const theme = useTheme();
  const { chatEmail, isLoading: isChatLoading } = useChatEmail();
  const { emailAddress } = useReadingEmail();
  const { contacts } = useContacts(emailAddress);

  // Get customer contact IDs for highlighting in the receivables dialog
  const matchedCustomerContactIds = useMemo(() => {
    if (!contacts) return [];
    return contacts
      .filter(({ type }) => type === ContactType.Customer || type === ContactType.SupplierCustomer)
      .map(({ id }) => id);
  }, [contacts]);

  const [internalMessage, setInternalMessage] = useState("");
  const [internalPrompt, setInternalPrompt] = useState("");
  const splitDraftInputs = draftValue !== undefined;
  const message = value ?? internalMessage;
  const setMessage = onValueChange ?? setInternalMessage;
  const prompt = promptValue ?? internalPrompt;
  const setPrompt = onPromptChange ?? setInternalPrompt;
  const draft = draftValue ?? "";
  const setDraft = onDraftChange ?? (() => undefined);
  const [currentStyle, setCurrentStyle] = useState<UserStyleModel | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string | undefined>(undefined);
  const [internalChatType, setInternalChatType] = useState<ChatTypeEnum>("Email");
  const currentType = chatType ?? internalChatType;
  const setCurrentType = onChatTypeChange ?? setInternalChatType;
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [selectedReceivables, setSelectedReceivables] = useState<SearchInvoiceReceivableResponseDetailedModel[]>([]);
  const [selectedOrganisationFiles, setSelectedOrganisationFiles] = useState<OrganisationFileResponse[]>([]);

  // Slash command state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [receivablesAnchorEl, setReceivablesAnchorEl] = useState<null | HTMLElement>(null);
  const [filesAnchorEl, setFilesAnchorEl] = useState<null | HTMLElement>(null);
  const [commandQuery, setCommandQuery] = useState<string>("");
  const inputRef = useRef<HTMLDivElement>(null);
  const [splitAnchorEl, setSplitAnchorEl] = useState<null | HTMLElement>(null);
  const [isReplyAll, setIsReplyAll] = useState(false);

  const isCurrentlyLoading = isLoading || isChatLoading;
  const isDraftDisabled = isCurrentlyLoading || prompt.length === 0;
  const isSendDisabled = isCurrentlyLoading || isEmptyEmailHtml(draft);
  const isRedraftDisabled = isCurrentlyLoading || isEmptyEmailHtml(draft) || prompt.length === 0;
  const isChatSendDisabled = isCurrentlyLoading || message.length === 0;

  const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handlePromptInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPrompt(newValue.slice(0, maxLength));

    const match = newValue.match(/(?:^|\s)\/([a-z0-9]*)$/i);

    if (match) {
      const query = match[1].toLowerCase();
      if ("receivable".startsWith(query) || "file".startsWith(query)) {
        setAnchorEl(inputRef.current);
        setCommandQuery(query);
      } else {
        setAnchorEl(null);
        setCommandQuery("");
      }
    } else {
      setAnchorEl(null);
      setCommandQuery("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setMessage(newValue.slice(0, maxLength));

    // Regex to match a slash command at the end of the input
    // Matches start of string or whitespace, followed by /, then optional alphanumeric chars
    const match = newValue.match(/(?:^|\s)\/([a-z0-9]*)$/i);

    if (match) {
      const query = match[1].toLowerCase();
      if ("receivable".startsWith(query) || "file".startsWith(query)) {
        setAnchorEl(inputRef.current);
        setCommandQuery(query);
      } else {
        setAnchorEl(null);
        setCommandQuery("");
      }
    } else {
      setAnchorEl(null);
      setCommandQuery("");
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCommandQuery("");
  };

  const handleReceivablesClick = () => {
    const source = splitDraftInputs ? prompt : message;
    const match = source.match(/(?:^|\s)\/([a-z0-9]*)$/i);
    if (match) {
      const lastIndex = source.lastIndexOf(match[0]);
      const newMessage = source.substring(0, lastIndex) + (match[0].startsWith(" ") ? " " : "");
      if (splitDraftInputs) {
        setPrompt(newMessage);
      } else {
        setMessage(newMessage);
      }
    }
    handleMenuClose();
    setReceivablesAnchorEl(inputRef.current);
  };

  const handleFilesClick = () => {
    const source = splitDraftInputs ? prompt : message;
    const match = source.match(/(?:^|\s)\/([a-z0-9]*)$/i);
    if (match) {
      const lastIndex = source.lastIndexOf(match[0]);
      const newMessage = source.substring(0, lastIndex) + (match[0].startsWith(" ") ? " " : "");
      if (splitDraftInputs) {
        setPrompt(newMessage);
      } else {
        setMessage(newMessage);
      }
    }
    handleMenuClose();
    setFilesAnchorEl(inputRef.current);
  };

  const handleEmailSend = async (isReplyAllParam?: boolean) => {
    const promptText = splitDraftInputs ? prompt : message;
    const isDisabled = splitDraftInputs ? isDraftDisabled : isChatSendDisabled;
    if (onSendMessage && !isDisabled) {
      await onSendMessage(
        promptText,
        currentStyle,
        currentType,
        selectedReceivables,
        selectedOrganisationFiles,
        isReplyAllParam,
        currentLanguage
      );
      if (!splitDraftInputs && value === undefined) {
        setMessage("");
        setSelectedReceivables([]);
        setSelectedOrganisationFiles([]);
      }
    }
  };

  const handleRedraft = async () => {
    if (onRedraft && !isRedraftDisabled) {
      await onRedraft(
        prompt,
        draft,
        currentStyle,
        currentType,
        selectedReceivables,
        selectedOrganisationFiles,
        false,
        currentLanguage
      );
    }
  };

  const handleSendDraft = async (isReplyAllParam?: boolean) => {
    if (onSendDraft && !isSendDisabled) {
      await onSendDraft(
        draft,
        currentStyle,
        currentType,
        selectedReceivables,
        selectedOrganisationFiles,
        isReplyAllParam,
        currentLanguage
      );
    }
  };

  const handleChatSend = async () => {
    if (isChatSendDisabled) return;

    // Add user message to chat history
    const userMessage: ChatMessage = {
      id: generateMessageId(),
      content: message,
      isUser: true,
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    const currentMessage = message;
    setMessage("");

    try {
      // Get email context for chat
      const emailId = (await getReadingEmailRestId()) || "";
      const subject = getReadingSubject() || "";
      const emailContent = (await getReadingEmailBody()) || "";

      // Send to chat API
      const response = await chatEmail({
        emailId,
        subject,
        email: emailContent,
        prompt: currentMessage,
        businessTenantId: null,
      });

      // Add AI response to chat history
      const aiMessage: ChatMessage = {
        id: generateMessageId(),
        content: response.response,
        isUser: false,
        timestamp: new Date(),
      };

      setChatMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: generateMessageId(),
        content: "Sorry, I encountered an error. Please try again.",
        isUser: false,
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleSend = async (isReplyAll?: boolean) => {
    if (currentType === "Email") {
      await handleEmailSend(isReplyAll || false);
    } else {
      await handleChatSend();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && event.ctrlKey) {
      if (currentType === "Email" && splitDraftInputs) {
        if (!isDraftDisabled) {
          void handleEmailSend(false);
        }
      } else if (!isChatSendDisabled) {
        void handleSend();
      }
    }
  };

  const getPlaceholderText = () => {
    if (currentType === "Chat") {
      return message.length === 0
        ? "Start a chat with Attelas AI..."
        : `Ask Attelas AI... (${message.length} / ${maxLength})`;
    }
    return message.length === 0 ? placeholder : `${placeholder} (${message.length} / ${maxLength})`;
  };

  const getPromptPlaceholderText = () => {
    return prompt.length === 0 ? placeholder : `${placeholder} (${prompt.length} / ${maxLength})`;
  };

  const handleSplitMenuToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSplitAnchorEl(splitAnchorEl ? null : event.currentTarget);
  };

  const handleSplitMenuClose = () => {
    setSplitAnchorEl(null);
  };

  const handleAskClick = async () => {
    await handleSend(false);
  };

  const handleDraftClick = async () => {
    setIsReplyAll(false);
    await handleEmailSend(false);
  };

  const handleSendClick = async () => {
    setIsReplyAll(false);
    await handleSendDraft(false);
    handleSplitMenuClose();
  };

  const handleSendReplyAllClick = async () => {
    setIsReplyAll(true);
    await handleSendDraft(true);
    handleSplitMenuClose();
  };

  const handleReplyClick = async () => {
    setIsReplyAll(false);
    if (splitDraftInputs && isDraftReady) {
      await handleSendDraft(false);
    } else {
      await handleSend(false);
    }
    handleSplitMenuClose();
  };

  const splitMenuOpen = Boolean(splitAnchorEl);

  const actionFooterSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1,
    py: 0.75,
    mt: 0.5,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  };

  const promptFooter = (
    <Box sx={actionFooterSx}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <LanguageSelect handleLanguageChange={setCurrentLanguage} />
        <Tooltip arrow title="Attach or /">
          <IconButton
            title="Attach or /"
            size="small"
            onClick={() => setAnchorEl(inputRef.current)}
            color="inherit"
            sx={{ color: "text.secondary" }}
          >
            <AttachmentIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ ml: "auto" }}>
        <LoadingButton
          color="gradient"
          variant="contained"
          loading={isLoading}
          onClick={handleDraftClick}
          disabled={isDraftDisabled || isLoading}
        >
          Draft
        </LoadingButton>
      </Box>
    </Box>
  );

  const draftFooter = (
    <Box sx={actionFooterSx}>
      <Box />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: "auto" }}>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <ButtonGroup variant="contained" disabled={isSendDisabled || isLoading}>
            <LoadingButton color="gradient" loading={isLoading} onClick={handleSendClick} endIcon={<SendIcon />}>
              Send
            </LoadingButton>
            <LoadingButton
              loading={isLoading}
              onClick={handleSplitMenuToggle}
              sx={{
                minWidth: 32,
                width: 32,
                maxWidth: 32,
                backgroundColor: "#9A1BF9",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "#8A19E0" },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
            >
              <ArrowDropDownIcon />
            </LoadingButton>
          </ButtonGroup>
          <Menu
            anchorEl={splitAnchorEl}
            open={splitMenuOpen}
            onClose={handleSplitMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <MenuItem onClick={handleSendClick}>Send Reply</MenuItem>
            <MenuItem onClick={handleSendReplyAllClick}>Send Reply to all</MenuItem>
          </Menu>
        </Box>
      </Stack>
    </Box>
  );

  const inputFooter = (
    <Box sx={actionFooterSx}>
      {currentType === "Email" ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <LanguageSelect handleLanguageChange={setCurrentLanguage} />
          <Tooltip arrow title="Attach or /">
            <IconButton
              title="Attach or /"
              size="small"
              onClick={() => setAnchorEl(inputRef.current)}
              color="inherit"
              sx={{ color: "text.secondary" }}
            >
              <AttachmentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Ctrl+Enter to send
        </Typography>
      )}

      <Box sx={{ ml: "auto" }}>
        {currentType === "Email" ? (
          <LoadingButton
            color="gradient"
            variant="contained"
            loading={isLoading}
            onClick={handleReplyClick}
            disabled={isChatSendDisabled || isLoading}
          >
            Draft
          </LoadingButton>
        ) : (
          <LoadingButton
            variant="contained"
            color="gradient"
            size="small"
            loading={isLoading}
            onClick={handleAskClick}
            disabled={isChatSendDisabled || isLoading}
            endIcon={<SendIcon />}
          >
            Ask
          </LoadingButton>
        )}
      </Box>
    </Box>
  );

  return (
    <Box>
      {currentType === "Chat" && (
        <Box sx={{ mb: 2 }}>
          <ChatHistory messages={chatMessages} isLoading={isChatLoading} />
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          mt: 2,
          mb: 2,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.action.hover,
            px: 2,
            py: 1,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
        >
          <ChatType handleTypeChange={setCurrentType} value={currentType} />
          {currentType === "Email" && <UserStyles handleStyleChange={setCurrentStyle} />}
        </Box>

        {(selectedReceivables.length > 0 || selectedOrganisationFiles.length > 0) && (
          <Box sx={{ mb: 1.5, px: 2, pt: 1.5 }}>
            {selectedReceivables.length > 0 && (
              <Box
                onClick={handleReceivablesClick}
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" }, mb: 0.5 }}
              >
                <ListOfSelectedReceivables receivables={selectedReceivables} />
              </Box>
            )}
            {selectedOrganisationFiles.length > 0 && (
              <Box onClick={handleFilesClick} sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
                <ListOfSelectedOrganisationFiles files={selectedOrganisationFiles} />
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ px: 0, pb: 0, pt: selectedReceivables.length || selectedOrganisationFiles.length ? 0 : 1.5 }}>
          {splitDraftInputs && currentType === "Email" ? (
            <Stack spacing={2} sx={{ px: 2, pb: 1.5 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  What should the email say?
                </Typography>
                <TextField
                  ref={inputRef}
                  placeholder={getPromptPlaceholderText()}
                  multiline
                  rows={3}
                  fullWidth
                  value={prompt}
                  onChange={handlePromptInputChange}
                  onKeyUp={handleKeyPress}
                  disabled={isCurrentlyLoading}
                  sx={{
                    "& fieldset.MuiOutlinedInput-notchedOutline": {
                      border: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                  helperText={promptFooter}
                  FormHelperTextProps={{ sx: { mx: 0 } }}
                />
              </Box>

              {isDraftReady && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Email preview
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <RichTextEditor
                      value={draft}
                      onChange={setDraft}
                      disabled={isCurrentlyLoading}
                      placeholder={draftReviewPlaceholder}
                      minHeight={160}
                    />
                    {isCurrentlyLoading && <DraftingOverlay />}
                  </Box>
                  {draftFooter}
                </Box>
              )}
            </Stack>
          ) : (
            <TextField
              ref={inputRef}
              placeholder={getPlaceholderText()}
              multiline
              rows={8}
              fullWidth
              value={message}
              onChange={handleInputChange}
              onKeyUp={handleKeyPress}
              sx={{
                "& fieldset.MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiInputBase-root": {
                  pb: 0,
                  px: 2,
                  pt: 0,
                  backgroundColor: "transparent",
                },
                "& .MuiFormHelperText-root": {
                  mx: 0,
                },
              }}
              helperText={inputFooter}
            />
          )}
        </Box>
      </Box>

      <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={handleMenuClose}>
          <Paper
            elevation={8}
            sx={{
              width: inputRef.current ? inputRef.current.clientWidth : undefined,
              mt: 1,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15)",
            }}
          >
            <MenuList>
              <MenuItem
                onClick={handleReceivablesClick}
                sx={{ display: commandQuery && !"receivable".startsWith(commandQuery) ? "none" : undefined }}
              >
                <ListItemIcon>
                  <AttachmentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="/receivable" secondary="Select receivables" />
              </MenuItem>
              <MenuItem
                onClick={handleFilesClick}
                sx={{ display: commandQuery && !"file".startsWith(commandQuery) ? "none" : undefined }}
              >
                <ListItemIcon>
                  <InsertDriveFileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="/file" secondary="Select organisation files" />
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>

      <ReceivablesSelectionPopper
        open={Boolean(receivablesAnchorEl)}
        anchorEl={receivablesAnchorEl}
        onClose={() => setReceivablesAnchorEl(null)}
        onConfirm={(selectedFiles) => {
          setSelectedReceivables(selectedFiles);
          setReceivablesAnchorEl(null);
        }}
        initialSelected={selectedReceivables}
        highlightedContactIds={matchedCustomerContactIds}
      />

      <OrganisationFilePicker
        open={!!filesAnchorEl}
        anchorEl={filesAnchorEl}
        onClose={() => setFilesAnchorEl(null)}
        onAdd={(files) => {
          setSelectedOrganisationFiles(files);
          setFilesAnchorEl(null);
        }}
        initialSelected={selectedOrganisationFiles}
      />
    </Box>
  );
};

const ListOfSelectedReceivables: React.FC<{ receivables: SearchInvoiceReceivableResponseDetailedModel[] }> = ({
  receivables,
}) => {
  const firstReceivables = receivables.slice(0, 1);
  return (
    <Typography variant="caption" color="text.secondary">
      Selected receivable{receivables.length > 1 ? "s" : ""}:
      {firstReceivables.map((file) => (
        <Chip key={file.id} label={file.invoiceNumber} size="small" sx={{ fontSize: 12, ml: 1 }} />
      ))}
      {receivables.length > 1 && (
        <Chip label={`+${receivables.length - 1} more`} size="small" sx={{ fontSize: 12, ml: 1 }} />
      )}
    </Typography>
  );
};

const ListOfSelectedOrganisationFiles: React.FC<{ files: OrganisationFileResponse[] }> = ({ files }) => {
  const firstFiles = files.slice(0, 1);
  const label = (f: OrganisationFileResponse) => f.displayName || f.fileName || "Unnamed";
  return (
    <Typography variant="caption" color="text.secondary">
      Selected file{files.length > 1 ? "s" : ""}:
      {firstFiles.map((f) => (
        <Chip key={f.id} label={label(f)} size="small" sx={{ fontSize: 12, ml: 1 }} />
      ))}
      {files.length > 1 && <Chip label={`+${files.length - 1} more`} size="small" sx={{ fontSize: 12, ml: 1 }} />}
    </Typography>
  );
};

export default EmailChat;
